import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autoplay, Pagination, A11y, Keyboard, Navigation } from 'swiper';
import classNames from 'classnames';
import { useAnimation, motion, MotionConfig, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import useUpdateEffect from '../custom-hooks/use-update-effect';
import { device } from '../../utils/styled-component-mixins';
import HomeHeroMainSlide from '../shared/HomeHeroMainSlide';
import HomeHeroThumbnailSlide from '../shared/HomeHeroThumbnailSlide';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/a11y';

const HomeHeroWrapper = styled.section`
  position: relative;
  overflow-x: hidden;

  @media ${device.desktop} {
    // White Gradient angled element that masks the rest of the slide
    .angled-background-mask {
      background-color: var(--white);
      bottom: 0;
      top: 0;
      content: '';
      position: absolute;
      right: -12rem;
      transform: skew(35deg);
      transform-origin: bottom right;
      width: 125%;
      box-shadow: inset -40px 0px 0px 0px #ffffff, inset -80px 0px 0px 0px #f9f9f9,
        inset -2000px 0px 0px 0px #f3f3f3;
      z-index: -3;

      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 30%,
          rgba(255, 255, 255, 1) 100%
        );
      }
    }

    // Red angled element that lies over the image
    .image-mask {
      background-color: var(--bdo-red);
      content: '';
      height: 500rem;
      left: 100vw;
      position: absolute;
      top: 16rem;
      transform: rotate(35deg);
      transform-origin: top left;
      width: 500rem;
      z-index: -4;
    }
  }

  // Thumbnail Swiper that renders on desktop only
  .thumbs-wrapper {
    max-width: 55.5rem;
    position: absolute;
    // 40px for navigation arrows
    left: 7rem;
    top: 50rem;
    width: 100%;
    z-index: 1;
    isolation: isolate;

    .container {
      padding-left: 0;
      margin-left: 0;
    }

    .swiper {
      margin-left: 0;
      margin-right: 0;
      z-index: 3;

      // Styles the thumbs swiper angle overlay
      &:after {
        content: '';
        height: 105%;
        right: -1.5rem;
        width: 25%;
        background-color: var(--white);
        position: absolute;
        z-index: 1;
        top: 0;
        clip-path: polygon(0 0, 100% 0%, 100% 100%, 47% 100%);
      }
    }

    .swiper-slide:hover {
      cursor: pointer;
    }

    .swiper-slide:not(.swiper-slide-active) {
      filter: grayscale(100%);
    }

    .hero-slide-progress-bar {
      border-bottom: var(--slate-transparent) 0.5rem solid;
    }

    .swiper-slide-active .hero-slide-progress-bar::after {
      ${(props) => (props.isPaused ? 'display: none;' : 'display: block;')}
      animation-duration: 6.5s;
      animation-name: slideProgress;
      animation-timing-function: linear;
      background-color: var(--bdo-red);
      bottom: 0;
      content: '';
      height: 0.5rem;
      left: 0;
      position: absolute;
      width: 100%;
    }
  }

  // The main Swiper... in case that wasn't clear by the class name
  .main-swiper {
    padding-bottom: 2rem;
    isolation: isolate;

    &::before,
    &:after {
      content: '';
      height: 100%;
      left: -3rem;
      position: absolute;
      top: 0;
      transform: translateY(-50%) skew(35deg);
      width: 20%;
      transform-origin: bottom right;
    }

    &:before {
      box-shadow: inset -40px 0px 0px 0px #f5f5f5, inset -800px 0px 0px 0px #ebebeb;
      z-index: -2;
    }

    &:after {
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0) 80%,
        rgba(255, 255, 255, 1) 100%
      );
      z-index: -1;
    }

    @media ${device.desktop} {
      padding-bottom: 25rem;
    }

    @media ${device.desktopLg} {
      padding-bottom: 45rem;
    }

    @media ${device.desktopXl} {
      padding-bottom: 70rem;
    }

    .swiper-wrapper {
      height: calc(100vh - var(--header-height));
      display: block;
    }

    .swiper-slide {
      position: absolute;
      left: -100%;

      &:not(.swiper-slide-active) {
        pointer-events: none;
      }
    }
  }

  .swiper-slide {
    height: auto;
  }

  // The pagination only renders on mobile
  .swiper-pagination {
    bottom: 0.8rem;
  }

  // This is the main slide image / video
  .desktop-media {
    background-size: cover;
    background-repeat: no-repeat;
    bottom: 0;
    height: 100vh;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -5;

    &.video {
      left: unset;
    }
  }

  // Bouncing button that anchors to the next section of the page
  .hero-skip {
    --play-state: ${(props) => (props.isPaused ? 'paused' : 'running')};
    position: absolute;
    height: 4rem;
    width: 4rem;
    border-radius: 50%;
    border: 2px solid var(--slate);
    top: calc(100vh - var(--header-height) - 10rem);
    left: 50%;
    transform: translateX(-50%);
    z-index: 9;

    &.animated {
      animation-play-state: var(--play-state);
      animation-duration: 3s;
      animation-name: bounce;
      animation-timing-function: ease-in-out;
      animation-iteration-count: infinite;
    }

    .caret {
      color: var(--slate);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) rotate(45deg);
      width: 0.8rem;
      height: 0.8rem;
      border-color: var(--slate);
      border-bottom: 2px solid;
      border-right: 2px solid;
    }
  }

  // Wrapper to allow for the correct positioning of the prev / next buttons
  .positioning-container {
    position: relative;
  }

  // Thumbnail Swiper navigation buttons
  .prev-slide,
  .next-slide {
    border: none;
    border-top: 0.2rem solid var(--slate);
    border-right: 0.2rem solid var(--slate);
    height: 2rem;
    position: absolute;
    top: 50%;
    width: 2rem;
    z-index: 3;

    &.swiper-button-disabled {
      border-color: var(--grey-2);

      &:hover {
        cursor: default;
      }
    }
  }

  .prev-slide {
    left: -4rem;
    transform: translateY(-50%) rotate(-135deg);
  }

  .next-slide {
    right: 3rem;
    transform: translateY(-50%) rotate(45deg);
  }

  .play-pause {
    position: absolute;
    top: 60rem;
    left: 6.5rem;
    z-index: 1;
    display: flex;
    padding: 0;
    border: none;
  }

  // Mobile slider styles
  .home-slider {
    --pagination-bullet-height: 2rem;

    .btn-wrapper {
      @media ${device.desktop} {
        margin-left: 1.5rem;
        margin-right: 1.5rem;
      }
    }

    .swiper {
      padding-bottom: var(--pagination-bullet-height);
    }

    .swiper-wrapper {
      height: calc(100vh - var(--header-height) - var(--pagination-bullet-height));
    }
  }

  // Keyframe animation that animates the red progress bar on the active thumbnail
  @keyframes slideProgress {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }

  // Keyframe animation for the bouncing button that anchors to the next section of the page
  @keyframes bounce {
    0% {
      transform: translateY(0) translateX(-50%);
    }
    25% {
      transform: translateY(10px) translateX(-50%);
    }
    50% {
      transform: translateY(0) translateX(-50%);
    }
    100% {
      transform: translateY(0) translateX(-50%);
    }
  }
`;

// Create a component to handle the media for desktop so we can store them outside the actual slides
const DesktopMedia = forwardRef(
  ({ slideMedia, slideMediaType, controls, isPaused, videoPoster }, videoRef) => {
    const shouldReduceMotion = useReducedMotion();

    const media = useMemo(() => {
      switch (slideMediaType) {
        case 'video':
          return (
            <video
              ref={videoRef}
              key={slideMedia}
              loop
              muted
              className="desktop-media video"
              autoPlay={!isPaused}
              poster={videoPoster}
            >
              <source src={slideMedia} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          );
        case 'image':
          return (
            <div className="desktop-media" style={{ backgroundImage: `url("${slideMedia}")` }} />
          );
        default:
          return null;
      }
    }, [slideMediaType, videoRef, isPaused, slideMedia, videoPoster]);

    return (
      <>
        {media}
        <motion.div
          className="desktop-media"
          initial={shouldReduceMotion ? { background: 'hsla(0 0 0 0)' } : null}
          transition={{ ease: [0.23, 1, 0.32, 1] }}
          variants={{
            slideTransitionNextStart: isPaused ? { background: 'hsla(0 0 0 1)' } : null,
            slideTransitionPrevStart: isPaused ? { background: 'hsla(0 0 0 1)' } : null,
            slideTransitionAfter: isPaused ? { background: 'hsla(0 0 0 0)' } : null
          }}
          animate={controls}
        ></motion.div>
      </>
    );
  }
);

const HomeHeroComponent = (props) => {
  /** @type {[import('swiper').default, import('react').Dispatch<import('swiper').default>]} */
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const isDesktop = useMediaQuery(device.desktop);
  const shouldReduceMotion = useReducedMotion();
  const [isPausedByPlayPauseButton, setIsPausedByPlayPauseButton] = useState(false);
  const [isPausedByNavigation, setIsPausedByNavigation] = useState(false);
  const [itemsState, setItemsState] = useState(props.items);
  const isPaused = isPausedByPlayPauseButton || isPausedByNavigation;
  const videoRef = useRef(null);

  const setItems = () => {
    let slideItems = props.items;
    while (slideItems.length !== 0 && slideItems.length < 4 && props.items && props.items.length) {
      slideItems = slideItems.concat(props.items.map((item) => item));
    }
    return slideItems;
  };

  useEffect(() => {
    setItemsState(setItems());
  }, [props.item]);

  useEffect(() => {
    setIsPausedByPlayPauseButton(shouldReduceMotion);
  }, [shouldReduceMotion]);

  const onNavigationOut = useCallback(() => setIsPausedByNavigation(false), []);
  const onNavigationIn = useCallback(() => setIsPausedByNavigation(true), []);

  useEffect(() => {
    /** @type {HTMLUListElement} */
    const navigationItems = document.querySelectorAll('ul.menu-list li.menu-list-item');
    if (navigationItems.length) {
      navigationItems.forEach((navigationItem) => {
        navigationItem.addEventListener('mouseover', onNavigationIn);
        navigationItem.addEventListener('mouseout', onNavigationOut);
      });
    }

    return () => {
      if (navigationItems.length) {
        navigationItems.forEach((navigationItem) => {
          navigationItem.removeEventListener('mouseover', onNavigationIn);
          navigationItem.removeEventListener('mouseout', onNavigationOut);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPaused) {
      videoRef.current?.pause();
      thumbsSwiper?.autoplay?.stop();
    } else {
      videoRef.current?.play();
      thumbsSwiper?.autoplay?.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused]);

  // Kill the thumbs swiper on mobile
  useUpdateEffect(() => {
    if (!isDesktop) {
      setMainSwiper(null);
      setThumbsSwiper(null);
    }
  }, [isDesktop]);

  // Fix for Swiper's lack of accessibility support.
  // ============================================================
  // We need to manually set the tabindex of each link on each slide so keyboard navigation
  // doesn't take the user to the next main slide and break the layout
  const setFocusableLinks = () => {
    if (typeof window !== 'undefined') {
      const mainSwiperSlides = document.querySelectorAll(
        '.main-swiper .swiper-slide:not(.swiper-slide-duplicate)'
      );

      mainSwiperSlides.forEach((slide) => {
        if (slide.classList.contains('swiper-slide')) {
          const links = slide.querySelectorAll('a');
          links.forEach((link) => {
            link.setAttribute('tabindex', '-1');
          });
        }
        if (slide.classList.contains('swiper-slide-active')) {
          const links = slide.querySelectorAll('a');
          links.forEach((link) => {
            link.setAttribute('tabindex', '0');
          });
        }
      });
    }
  };
  setFocusableLinks();

  const [slideMedia, setSlideMedia] = useState(itemsState[0]?.media);
  const [slideMediaType, setSlideMediaType] = useState(itemsState[0]?.mediaType);
  const [videoPoster, setVideoPoster] = useState(itemsState[0]?.videoPoster);
  const controls = useAnimation();

  const setMedia = () => {
    const currentSlide = itemsState.find((item, index) => index === mainSwiper?.realIndex);
    setSlideMedia(currentSlide?.media);
    setSlideMediaType(currentSlide?.mediaType);
    setVideoPoster(currentSlide?.videoPoster);
  };

  const onSlideChangeTransitionStart = async () => {
    await controls.start('slideTransitionStart');
    setMedia();
    await controls.start('slideTransitionEnd');
    if (isPausedByPlayPauseButton) await controls.start('slideTransitionAfter');
  };

  return (
    <ServerStylesConditionalWrapper>
      <MotionConfig reducedMotion="user">
        <HomeHeroWrapper isPaused={isPaused}>
          {/* Swiper setup for desktop is very different from mobile so we break them up */}
          {isDesktop && (
            <>
              <motion.div
                initial={{ y: 240 }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: 'easeInOut' }}
                style={{ zIndex: 2, position: 'relative' }}
              >
                {!shouldReduceMotion && (
                  <button
                    className="play-pause"
                    aria-label={
                      isPausedByPlayPauseButton
                        ? 'Play video and animations'
                        : 'Pause video and animations'
                    }
                    onClick={() => {
                      setIsPausedByPlayPauseButton(
                        (previousIsPausedByPlayPauseButton) => !previousIsPausedByPlayPauseButton
                      );
                    }}
                  >
                    <span className="material-icons-round">
                      {isPausedByPlayPauseButton ? 'play_arrow' : 'pause'}
                    </span>
                  </button>
                )}
              </motion.div>
              {/* Main Swiper that has the large slides */}
              <Swiper
                className="main-swiper"
                a11y
                virtualTranslate
                slidesPerView={1}
                navigation={false}
                allowTouchMove={false}
                modules={[A11y]}
                onSwiper={setMainSwiper}
                onSlideChangeTransitionStart={onSlideChangeTransitionStart}
              >
                <DesktopMedia
                  ref={videoRef}
                  isPaused={isPaused}
                  slideMedia={slideMedia}
                  slideMediaType={slideMediaType}
                  controls={controls}
                  videoPoster={videoPoster}
                />
                {/* Red angled element that lies over the image */}
                <motion.div
                  className="image-mask"
                  initial={{ top: '16rem' }}
                  variants={{
                    slideTransitionStart: shouldReduceMotion
                      ? null
                      : { top: '-160rem', transition: { duration: 1 } },
                    slideTransitionEnd: shouldReduceMotion
                      ? { top: '16rem', transition: { duration: 0 } }
                      : { top: '16rem', transition: { duration: 1 } }
                  }}
                  animate={controls}
                />
                {/* White Gradient angled element that masks the rest of the slide */}
                <div className="angled-background-mask"></div>
                {itemsState.map((item, index) => (
                  <SwiperSlide key={index}>
                    <HomeHeroMainSlide
                      index={index}
                      item={item}
                      controls={controls}
                      isPaused={isPausedByPlayPauseButton}
                    />
                  </SwiperSlide>
                ))}
                {/* Bouncy button that scrolls the page to the next section */}
                <a
                  href="#home-main"
                  aria-label="Skip to main content"
                  className={classNames('hero-skip', { animated: !shouldReduceMotion })}
                >
                  <span className="caret"></span>
                </a>
              </Swiper>

              {/* Thumbnail Swiper that controls the main Swiper */}
              <div className="thumbs-wrapper">
                <div className="container">
                  <div className="positioning-container">
                    <motion.div
                      initial={{ y: 200 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 1, ease: 'easeInOut' }}
                    >
                      <Swiper
                        a11y
                        loop
                        grabCursor
                        watchSlidesProgress
                        slideToClickedSlide
                        spaceBetween={8}
                        slidesPerView={4}
                        shortSwipes={false}
                        onSwiper={setThumbsSwiper}
                        loopedSlides={itemsState.length}
                        modules={[
                          A11y,
                          Keyboard,
                          Navigation,
                          ...(shouldReduceMotion ? [] : [Autoplay])
                        ]}
                        keyboard={{ onlyInViewport: true }}
                        navigation={{ nextEl: '.next-slide', prevEl: '.prev-slide' }}
                        autoplay={
                          shouldReduceMotion
                            ? undefined
                            : {
                                delay: 7000,
                                disableOnInteraction: true
                              }
                        }
                        onSlideChange={(thumb) => {
                          setFocusableLinks();
                          mainSwiper?.slideTo(thumb.realIndex);
                        }}
                        onSlideChangeTransitionEnd={() => {
                          if (!isPausedByPlayPauseButton && !isPausedByNavigation) {
                            thumbsSwiper?.autoplay?.start();
                          }
                        }}
                      >
                        {itemsState.map((item, index) => (
                          <SwiperSlide key={index} tabIndex="0">
                            <HomeHeroThumbnailSlide item={item} />
                            {!shouldReduceMotion && <div className="hero-slide-progress-bar"></div>}
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      {/* Manually add prev/next buttons so they are outside of the Swiper and can be positioned correctly */}
                      <button
                        className="prev-slide"
                        aria-label="previous"
                        onClick={() => setIsPausedByPlayPauseButton(true)}
                      ></button>
                      <button
                        className="next-slide"
                        aria-label="next"
                        onClick={() => setIsPausedByPlayPauseButton(true)}
                      ></button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Mobile Swiper is much more simple */}
          {!isDesktop && (
            <div className="home-slider">
              <Swiper
                pagination={{ clickable: false }}
                slidesPerView={1}
                modules={[Pagination]}
                init={false}
                loop
              >
                {itemsState.map((item, index) => (
                  <SwiperSlide key={index}>
                    <HomeHeroMainSlide item={item} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </HomeHeroWrapper>
      </MotionConfig>
    </ServerStylesConditionalWrapper>
  );
};

export default HomeHeroComponent;
